/**
 * Système de thèmes — définitions partagées.
 * Utilisé par AssociationContext (application CSS vars) et AssociationSettings (UI picker).
 */

export interface AppTheme {
  /** Identifiant du thème prédéfini. null = thème personnalisé. */
  id: string | null;
  /** Couleur principale (boutons, liens, titres accent). */
  primary: string;
  /** Texte sur fond primary. */
  primaryText: string;
  /** Fond du header / navbar. */
  headerBg: string;
  /** Texte et icônes dans le header. */
  headerText: string;
  /** Accent des tableaux de bord (menus actifs, indicateurs, badges). */
  accent: string;
  /** Fond des cartes / panneaux. */
  surface: string;
  /** Couleur de police principale du corps (paragraphes, titres de page). */
  textColor: string;
  /** Fond général de la page (derrière les cartes et panneaux). */
  background: string;
  /** Couleur des bordures, séparateurs et contours. */
  borderColor: string;
}

export interface PresetTheme extends AppTheme {
  name: { ar: string; fr: string };
  emoji: string;
}

/** Thème personnalisé sauvegardé par l'administrateur (max 3). */
export interface SavedCustomTheme {
  id: string;
  name: string;
  primary: string;
  primaryText: string;
  textColor: string;
  headerBg: string;
  headerText: string;
  accent: string;
  surface: string;
  background: string;
  borderColor: string;
}

export const PRESET_THEMES: PresetTheme[] = [
  // ── Classiques ──────────────────────────────────────────────────────────────
  {
    id: 'green-classic',
    name: { ar: 'أخضر كلاسيكي', fr: 'Vert Classique' },
    emoji: '🌿',
    primary:     '#2E7D32',
    primaryText: '#FFFFFF',
    headerBg:    '#1B5E20',
    headerText:  '#FFFFFF',
    accent:      '#43A047',
    surface:     '#FFFFFF',
    textColor:   '#1B2E1C',
    background:  '#F1F8E9',
    borderColor: '#C8E6C9',
  },
  {
    id: 'blue-pro',
    name: { ar: 'أزرق مهني', fr: 'Bleu Professionnel' },
    emoji: '💼',
    primary:     '#1565C0',
    primaryText: '#FFFFFF',
    headerBg:    '#0D47A1',
    headerText:  '#FFFFFF',
    accent:      '#1976D2',
    surface:     '#FFFFFF',
    textColor:   '#0D1B2A',
    background:  '#E3F2FD',
    borderColor: '#BBDEFB',
  },
  {
    id: 'purple-modern',
    name: { ar: 'بنفسجي عصري', fr: 'Violet Moderne' },
    emoji: '✨',
    primary:     '#5E35B1',
    primaryText: '#FFFFFF',
    headerBg:    '#4527A0',
    headerText:  '#EDE7F6',
    accent:      '#7E57C2',
    surface:     '#FFFFFF',
    textColor:   '#1A0533',
    background:  '#EDE7F6',
    borderColor: '#D1C4E9',
  },
  {
    id: 'teal',
    name: { ar: 'تركوازي', fr: 'Turquoise' },
    emoji: '🌊',
    primary:     '#00695C',
    primaryText: '#FFFFFF',
    headerBg:    '#004D40',
    headerText:  '#E0F2F1',
    accent:      '#00897B',
    surface:     '#FFFFFF',
    textColor:   '#002722',
    background:  '#E0F2F1',
    borderColor: '#B2DFDB',
  },
  {
    id: 'bordeaux',
    name: { ar: 'بوردو', fr: 'Bordeaux' },
    emoji: '🍷',
    primary:     '#AD1457',
    primaryText: '#FFFFFF',
    headerBg:    '#880E4F',
    headerText:  '#FCE4EC',
    accent:      '#C2185B',
    surface:     '#FFFFFF',
    textColor:   '#2D0014',
    background:  '#FCE4EC',
    borderColor: '#F8BBD0',
  },

  // ── Mode sombre ─────────────────────────────────────────────────────────────
  {
    id: 'dark',
    name: { ar: 'داكن', fr: 'Sombre' },
    emoji: '🌙',
    primary:     '#90CAF9',
    primaryText: '#0D1B2A',
    headerBg:    '#0D1117',
    headerText:  '#CDD9E5',
    accent:      '#4FC3F7',
    surface:     '#1C2128',
    textColor:   '#CDD9E5',
    background:  '#161B22',
    borderColor: '#30363D',
  },

  // ── Nouveaux thèmes ─────────────────────────────────────────────────────────
  {
    id: 'orange-energy',
    name: { ar: 'برتقالي نشيط', fr: 'Orange Énergique' },
    emoji: '🔥',
    primary:     '#E65100',
    primaryText: '#FFFFFF',
    headerBg:    '#BF360C',
    headerText:  '#FFFFFF',
    accent:      '#FF6D00',
    surface:     '#FFFFFF',
    textColor:   '#1A0900',
    background:  '#FFF3E0',
    borderColor: '#FFCCBC',
  },
  {
    id: 'night',
    name: { ar: 'ليل عميق', fr: 'Nuit Profonde' },
    emoji: '🌑',
    primary:     '#BB86FC',
    primaryText: '#1A0033',
    headerBg:    '#0D0D1A',
    headerText:  '#E0E0E0',
    accent:      '#03DAC6',
    surface:     '#1E1E2E',
    textColor:   '#E2E2F0',
    background:  '#13131F',
    borderColor: '#2C2C44',
  },
  {
    id: 'golden',
    name: { ar: 'ذهبي فاخر', fr: 'Or Premium' },
    emoji: '🏆',
    primary:     '#B8860B',
    primaryText: '#FFFFFF',
    headerBg:    '#4E342E',
    headerText:  '#FFD54F',
    accent:      '#F9A825',
    surface:     '#FFFFFF',
    textColor:   '#2E1800',
    background:  '#FFFDE7',
    borderColor: '#FFE082',
  },
  {
    id: 'indigo',
    name: { ar: 'نيلي عميق', fr: 'Indigo Profond' },
    emoji: '💎',
    primary:     '#283593',
    primaryText: '#FFFFFF',
    headerBg:    '#1A237E',
    headerText:  '#C5CAE9',
    accent:      '#3949AB',
    surface:     '#FFFFFF',
    textColor:   '#0D1240',
    background:  '#E8EAF6',
    borderColor: '#C5CAE9',
  },
  {
    id: 'rose',
    name: { ar: 'وردي أنيق', fr: 'Rose Élégant' },
    emoji: '🌸',
    primary:     '#D81B60',
    primaryText: '#FFFFFF',
    headerBg:    '#880E4F',
    headerText:  '#FFFFFF',
    accent:      '#F06292',
    surface:     '#FFFFFF',
    textColor:   '#3D001A',
    background:  '#FCE4EC',
    borderColor: '#F8BBD0',
  },
  {
    id: 'slate',
    name: { ar: 'رمادي معدني', fr: 'Gris Ardoise' },
    emoji: '🪨',
    primary:     '#455A64',
    primaryText: '#FFFFFF',
    headerBg:    '#263238',
    headerText:  '#ECEFF1',
    accent:      '#607D8B',
    surface:     '#FFFFFF',
    textColor:   '#1C2A33',
    background:  '#ECEFF1',
    borderColor: '#CFD8DC',
  },
  {
    id: 'sky',
    name: { ar: 'سماوي منعش', fr: 'Ciel Frais' },
    emoji: '☁️',
    primary:     '#0288D1',
    primaryText: '#FFFFFF',
    headerBg:    '#01579B',
    headerText:  '#E1F5FE',
    accent:      '#29B6F6',
    surface:     '#FFFFFF',
    textColor:   '#011F35',
    background:  '#E1F5FE',
    borderColor: '#B3E5FC',
  },
];

export const DEFAULT_THEME: AppTheme = PRESET_THEMES[0];

/** Retourne le preset correspondant à un id, ou null si c'est un thème custom. */
export function findPreset(id: string | null): PresetTheme | null {
  if (!id) return null;
  return PRESET_THEMES.find(t => t.id === id) ?? null;
}

/** Retourne un AppTheme depuis les données brutes de l'API. */
export function parseTheme(raw: any, fallbackPrimaryColor?: string): AppTheme {
  if (raw && typeof raw === 'object' && raw.primary) {
    return {
      id:          raw.id          ?? null,
      primary:     raw.primary     ?? DEFAULT_THEME.primary,
      primaryText: raw.primaryText ?? DEFAULT_THEME.primaryText,
      headerBg:    raw.headerBg    ?? raw.primary ?? DEFAULT_THEME.headerBg,
      headerText:  raw.headerText  ?? DEFAULT_THEME.headerText,
      accent:      raw.accent      ?? raw.primary ?? DEFAULT_THEME.accent,
      surface:     raw.surface     ?? DEFAULT_THEME.surface,
      textColor:   raw.textColor   ?? DEFAULT_THEME.textColor,
      background:  raw.background  ?? DEFAULT_THEME.background,
      borderColor: raw.borderColor ?? DEFAULT_THEME.borderColor,
    };
  }
  // Fallback : construire un thème minimal depuis primary_color
  const p = fallbackPrimaryColor || DEFAULT_THEME.primary;
  return { ...DEFAULT_THEME, id: null, primary: p, headerBg: p, accent: p };
}
